/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CluUtteranceSwagger } from 'src/clu/core/services/cluUtteranceSwagger';
import { CluProjectDataSwagger, CluProjectExportMetaDataSwagger } from 'src/clu/core/services/projects/cluProjectSwaggers';
import { createOperationCallAndPoll } from 'src/core/services/api/createOperationCall';
import { httpService } from 'src/core/services/api/httpService';
import { OperationStatusBaseSwagger } from 'src/core/services/api/operationSwagger';
import { ExportedEntitySwagger, PrebuiltEntitySwagger } from 'src/core/types/EntitySwagger';
import { ResourceListResults } from 'src/core/types/ResourceListTypes';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { DeepPartial } from 'utility-types';

export const getCluEntityService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment({ apiName: 'clu' });

    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .useCustomHeader('format', 'clu')
        .build();

    const getUrl = (projectName?: string) =>
        urlBuilder()
            .baseUrl(apiUrl)
            .project(projectName)
            .queryParam('api-version', LuisConstants.Clu.AuthoringApiVersion);

    /**
     * Updates entities from the project. Since the backend uses a
     * batch approach, this function assumes that the entities array
     * given already has the entities updated for a direct
     * "functional style" replace.
     */
    const updateEntities = async ({
        utterances,
        entitiesData,
        projectRequiredMetaData
    }: {
        entitiesData: ExportedEntitySwagger[];
        utterances?: CluUtteranceSwagger[];
        projectRequiredMetaData: CluProjectExportMetaDataSwagger;
    }) => {
        const { projectName, projectKind } = projectRequiredMetaData.metadata;

        const url = getUrl(projectName)
            .resource(':import')
            .build();

        const partialData: DeepPartial<CluProjectDataSwagger> = {
            ...projectRequiredMetaData,
            assets: { utterances, projectKind, entities: entitiesData }
        };

        await createOperationCallAndPoll<OperationStatusBaseSwagger>(
            httpService.post(url, partialData, apiOptions),
            apiOptions,
            undefined,
            response => (<Record<string, string>>response.headers)[LuisConstants.OperationHeaders.LocationHeader]
        );
    };

    const getAvailablePrebuilts = async ({
        nextLink,
        projectCulture,
        isMultiLingual
    }: {
        nextLink?: string;
        projectCulture: string;
        isMultiLingual: boolean;
    }): Promise<ResourceListResults<PrebuiltEntitySwagger>> => {
        const url =
            nextLink ||
            getUrl()
                .resource('global', 'prebuilt-entities')
                .queryParam('language', projectCulture)
                .queryParam('multilingual', String(isMultiLingual))
                .build();

        const { data } = await httpService.get<ResourceListResults<PrebuiltEntitySwagger>>(url, apiOptions);

        return data;
    };

    return { updateEntities, getAvailablePrebuilts };
});
